/**
 * Recursive drill-down navigation for the mobile menu drawer.
 */
(function () {
  'use strict';

  const ROOT = 'root';

  class MenuDrawerDrilldown {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.js-menu-drawer-drilldown-track');
      this.refreshPanels();
      this.stack = [ROOT];
      this.activePanel = ROOT;
      this.drawer = document.getElementById('menuDrawer');

      this.onClick = this.onClick.bind(this);
      this.onDrawerChange = this.onDrawerChange.bind(this);
      this.onDrawerOpen = this.onDrawerOpen.bind(this);
      this.onAnimationEnd = this.onAnimationEnd.bind(this);

      root.addEventListener('click', this.onClick);
      root.addEventListener('animationend', this.onAnimationEnd);

      if (this.drawer) {
        this.drawerObserver = new MutationObserver(this.onDrawerChange);
        this.drawerObserver.observe(this.drawer, {
          attributes: true,
          attributeFilter: ['hidden', 'aria-hidden', 'open']
        });
        this.drawer.addEventListener('opened', this.onDrawerOpen);
      }

      this.disableLegacyAccordion();
      this.update(false);
    }

    refreshPanels() {
      this.panels = Array.from(
        this.root.querySelectorAll('.menu-drawer-drilldown__panel')
      );
      this.panelMap = new Map(
        this.panels.map((panel) => [panel.dataset.panel, panel])
      );
    }

    disableLegacyAccordion() {
      const drawer = this.drawer || document.getElementById('menuDrawer');
      if (!drawer) return;

      drawer.querySelectorAll('details[data-level]').forEach((detail) => {
        detail.removeAttribute('open');
      });

      drawer.querySelectorAll('sht-menu-header').forEach((header) => {
        if (!header.closest('.menu-drawer-drilldown')) {
          header.querySelectorAll('details').forEach((detail) => {
            detail.removeAttribute('open');
          });
        }
      });
    }

    onDrawerOpen() {
      this.disableLegacyAccordion();
      this.reset();
    }

    onClick(event) {
      const trigger = event.target.closest('[data-drill-target]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(trigger.dataset.drillTarget);
        return;
      }

      if (event.target.closest('.js-menu-drawer-drilldown-back')) {
        event.preventDefault();
        event.stopPropagation();
        this.goBack();
      }
    }

    onDrawerChange() {
      if (!this.drawer) return;
      const isClosed =
        this.drawer.hasAttribute('hidden') ||
        this.drawer.getAttribute('aria-hidden') === 'true' ||
        !this.drawer.hasAttribute('open');
      if (isClosed) this.reset();
    }

    goTo(panelId) {
      const panel = this.panelMap.get(panelId);
      if (!panel) return;

      this.navDirection = 'forward';
      this.stack.push(panelId);
      this.update(true);

      const inner = panel.querySelector('.menu-drawer-drilldown__panel-inner');
      if (inner) inner.scrollTop = 0;
    }

    goBack() {
      if (this.stack.length <= 1) return;
      this.navDirection = 'back';
      this.stack.pop();
      this.update(true);
    }

    reset() {
      this.stack = [ROOT];
      this.navDirection = 'reset';
      this.disableLegacyAccordion();
      this.update(false);
    }

    onAnimationEnd(event) {
      if (!event.animationName.startsWith('menu-drawer-drilldown-')) return;

      const panel = event.target;
      if (!panel.classList.contains('menu-drawer-drilldown__panel')) return;
      if (!this.root.contains(panel)) return;

      panel.classList.remove(
        'is-entering',
        'is-entering-back',
        'is-leaving',
        'is-leaving-back'
      );
    }

    update(animate) {
      const current = this.stack[this.stack.length - 1];
      const previous = this.activePanel;
      const direction = animate ? this.navDirection : 'reset';

      this.track.style.transform = '';

      this.panels.forEach((panel) => {
        const panelId = panel.dataset.panel;
        const isActive = panelId === current;
        const isPrevious = panelId === previous;

        panel.classList.remove(
          'is-entering',
          'is-entering-back',
          'is-leaving',
          'is-leaving-back'
        );
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.style.pointerEvents = isActive ? '' : 'none';

        if (!animate || direction === 'reset') return;

        if (isActive) {
          panel.classList.add(
            direction === 'forward' ? 'is-entering' : 'is-entering-back'
          );
        } else if (isPrevious && direction === 'back') {
          panel.classList.add('is-leaving-back');
        }
      });

      this.activePanel = current;
      this.navDirection = null;
    }

    destroy() {
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('animationend', this.onAnimationEnd);
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.drawer) {
        this.drawer.removeEventListener('opened', this.onDrawerOpen);
      }
    }
  }

  function init() {
    const drawer = document.getElementById('menuDrawer');
    if (drawer) {
      drawer.querySelectorAll('details[data-level]').forEach((el) => {
        el.removeAttribute('open');
      });
    }

    document.querySelectorAll('.js-menu-drawer-drilldown').forEach((el) => {
      if (el.menuDrawerDrilldown) {
        el.menuDrawerDrilldown.refreshPanels();
        return;
      }
      el.menuDrawerDrilldown = new MenuDrawerDrilldown(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
